import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiSWR } from "@/hooks/useApiSWR";
import { useEmailToTaskForm } from "@/hooks/useEmailToTaskForm";
import {
	getEmailToTaskSettings,
	updateEmailToTaskSettings,
} from "@/services/email-to-task.service";
import { SectionBlock } from "./SectionBlock";
import { GeneralCard } from "./email-to-task/GeneralCard";
import { SetupChecklist } from "./email-to-task/SetupChecklist";
import { ResendCard } from "./email-to-task/ResendCard";
import { HubCard } from "./email-to-task/HubCard";
import { SenderRulesCard } from "./email-to-task/SenderRulesCard";
import { TaskDefaultsCard } from "./email-to-task/TaskDefaultsCard";
import { ReviewNotifyCard } from "./email-to-task/ReviewNotifyCard";

export function EmailToTaskSection() {
	const { data: settings, error, isLoading, mutate } = useApiSWR(
		"email-to-task:settings",
		getEmailToTaskSettings,
		{ revalidateOnFocus: false },
	);
	const formApi = useEmailToTaskForm(settings);
	const [saving, setSaving] = useState(false);

	if (error) {
		return (
			<SectionBlock title="Email-to-Task">
				<p className="text-sm text-danger">{error.message || "Failed to load settings."}</p>
				<Button size="sm" variant="outline" className="mt-3" onClick={() => mutate()}>
					Retry
				</Button>
			</SectionBlock>
		);
	}

	if (isLoading || !settings || !formApi.form) {
		return (
			<div className="space-y-6" aria-busy="true">
				{[0, 1, 2].map((i) => (
					<Skeleton key={i} className="h-48 w-full rounded-xl" />
				))}
			</div>
		);
	}

	async function handleSave() {
		setSaving(true);
		try {
			const updated = await updateEmailToTaskSettings(formApi.patch);
			await mutate(updated, { revalidate: false });
			formApi.reset(updated);
			toast.success("Email-to-Task settings saved.");
		} catch (err) {
			toast.error((err as Error).message || "Failed to save settings.");
		} finally {
			setSaving(false);
		}
	}

	const api = { ...formApi, form: formApi.form, settings, disabled: saving };

	return (
		<div className="space-y-6">
			<GeneralCard {...api} />
			<SetupChecklist settings={settings} />
			<ResendCard {...api} />
			<HubCard {...api} />
			<SenderRulesCard {...api} />
			<TaskDefaultsCard {...api} />
			<ReviewNotifyCard {...api} />

			<div className="sticky bottom-4 z-10 flex items-center justify-end gap-3 rounded-xl border border-border bg-surface/95 px-4 py-3 shadow-sm backdrop-blur">
				<p className="mr-auto text-xs text-muted">
					{formApi.dirty ? "You have unsaved changes." : "All changes saved."}
				</p>
				<Button
					size="sm"
					variant="outline"
					onClick={() => formApi.reset(settings)}
					disabled={!formApi.dirty || saving}
				>
					Discard
				</Button>
				<Button size="sm" onClick={handleSave} disabled={!formApi.dirty || saving}>
					{saving ? "Saving..." : "Save Changes"}
				</Button>
			</div>
		</div>
	);
}
